// HR's queue of Talk to HR requests: New, In progress, Resolved. Opening one shows who asked,
// their note and the conversation; HR moves it along and writes either a note for colleagues or a
// comment the employee sees on their request in Ask HR. Notes are the default; employees never
// see them.

import { Fragment, useEffect, useState } from "react";
import { CATEGORIES, STATUS_LABELS, type Escalation, type EscalationStatus } from "@ask-hr/core/escalations";
import { api } from "../api.js";
import { Seg } from "../components/Seg.js";
import { navigate } from "../router.js";

type Filter = EscalationStatus | "all";

// Search covers who asked, what about, their note and conversation, and HR's notes and comments.
const searchText = (r: Escalation) =>
  [
    r.requester.name,
    r.requester.email,
    CATEGORIES[r.category],
    r.location,
    r.note,
    ...r.excerpt.map((t) => t.text),
    ...r.history.map((h) => `${h.by} ${h.note ?? ""}`),
  ]
    .join("\n")
    .toLowerCase();

const PILL: Record<EscalationStatus, string> = { new: "pill alert", in_progress: "pill", resolved: "pill ok" };
const when = (iso: string) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

export function RequestsPage({ openId }: { openId: string | null }) {
  const [requests, setRequests] = useState<Escalation[] | null>(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<Filter>("new");
  const [query, setQuery] = useState("");

  const fetchAll = () => api<{ requests: Escalation[] }>("GET", "/api/admin/escalations").then((r) => r.requests);
  const reload = () => fetchAll().then(setRequests, () => setError(true));

  useEffect(() => {
    let live = true;
    fetchAll().then(
      (r) => live && setRequests(r),
      () => live && setError(true),
    );
    return () => {
      live = false;
    };
  }, []);

  const q = query.trim().toLowerCase();
  const matching = (requests ?? []).filter((r) => !q || searchText(r).includes(q));
  const counts = (s: Filter) => matching.filter((r) => s === "all" || r.status === s).length;
  const opened = openId ? requests?.find((r) => r.id === openId) : undefined;
  const shown = matching.filter((r) => filter === "all" || r.status === filter || r.id === openId);

  return (
    <section className="panel" aria-labelledby="req-h">
      <h2 id="req-h">Talk to HR requests</h2>
      <p className="sub">Each request is also emailed to hr@novara.com; reply to that email to answer the employee.</p>
      {error ? (
        <p className="notice" role="alert">
          Couldn't load requests. Try again.
        </p>
      ) : null}
      <div className="toolbar">
        <Seg
          label="Show"
          value={filter}
          onChange={setFilter}
          options={[
            ["new", `New (${counts("new")})`],
            ["in_progress", `In progress (${counts("in_progress")})`],
            ["resolved", `Resolved (${counts("resolved")})`],
            ["all", `All (${counts("all")})`],
          ]}
        />
        <label className="search">
          <span className="sr-only">Search requests</span>
          <input
            type="search"
            placeholder="Search by name, topic or text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>
      {requests === null ? (
        error ? null : <p className="sub">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="empty">{q ? `Nothing matches "${query.trim()}".` : "None."}</p>
      ) : (
        <div className="tablewrap tall">
          <table>
            <thead>
              <tr>
                <th scope="col">Received</th>
                <th scope="col">From</th>
                <th scope="col">About</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <Fragment key={r.id}>
                  <tr>
                    <td className="muted">{when(r.createdAt)}</td>
                    <td>
                      {r.requester.name}
                      <div className="muted small">
                        {r.location} · {r.channel === "slack" ? "Slack" : "Web"}
                        {r.emailed ? "" : " · email not sent"}
                        {r.hiddenAt ? " · removed from their list" : ""}
                      </div>
                    </td>
                    <td>{CATEGORIES[r.category]}</td>
                    <td>
                      <span className={PILL[r.status]}>{STATUS_LABELS[r.status]}</span>
                    </td>
                    <td className="right">
                      <button
                        type="button"
                        aria-expanded={opened?.id === r.id}
                        onClick={() => navigate(opened?.id === r.id ? "/admin/requests" : `/admin/requests/${r.id}`)}
                      >
                        {opened?.id === r.id ? "Close" : "Open"}
                      </button>
                    </td>
                  </tr>
                  {opened?.id === r.id ? (
                    <tr className="detail">
                      <td colSpan={5}>
                        <RequestDetail request={r} onChanged={() => void reload()} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function RequestDetail({ request: r, onChanged }: { request: Escalation; onChanged: () => void }) {
  const [note, setNote] = useState("");
  const [audience, setAudience] = useState<"hr" | "employee">("hr");
  const [busy, setBusy] = useState(false);
  const toEmployee = audience === "employee";

  async function update(status?: EscalationStatus) {
    setBusy(true);
    try {
      await api("PATCH", `/api/admin/escalations/${r.id}`, {
        ...(status ? { status } : {}),
        ...(note.trim() ? { note, toEmployee } : {}),
      });
      setNote("");
      setAudience("hr");
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="review">
      <p>
        <a href={`mailto:${r.requester.email}?subject=${encodeURIComponent(`Re: ${CATEGORIES[r.category]}`)}`}>{r.requester.email}</a>
      </p>
      {r.note ? (
        <>
          <h4>Their note</h4>
          <p className="prewrap">{r.note}</p>
        </>
      ) : null}
      {r.excerpt.length ? (
        <>
          <h4>The conversation with Ask HR</h4>
          <div className="extract">
            {r.excerpt.map((t, i) => (
              <p key={i} className="prewrap">
                <strong>{t.role === "user" ? r.requester.name : "Ask HR"}:</strong> {t.text}
              </p>
            ))}
          </div>
        </>
      ) : null}
      {r.hiddenAt ? (
        <p className="muted small">
          {r.requester.name} removed this from their list in Ask HR on {when(r.hiddenAt)}. They won't see new comments on it.
        </p>
      ) : null}
      <h4>History</h4>
      <ul className="history-events">
        {r.history.map((h, i) => (
          <li key={i}>
            <span className="muted">{when(h.at)}</span> · {h.by}
            {h.status ? ` · ${STATUS_LABELS[h.status]}` : ""}
            {h.note && h.visibility === "employee" ? (
              <>
                {" "}
                · <span className="pill">To {r.requester.name.split(" ")[0]}</span>
              </>
            ) : null}
            {h.note ? <div className="prewrap">{h.note}</div> : null}
          </li>
        ))}
      </ul>
      <div className="field">
        <span id={`note-h-${r.id}`}>Add a note or comment</span>
        <Seg
          label="Who sees it"
          value={audience}
          onChange={setAudience}
          options={[
            ["hr", "Note for HR only"],
            ["employee", `Comment to ${r.requester.name.split(" ")[0]}`],
          ]}
        />
        <textarea
          aria-labelledby={`note-h-${r.id}`}
          rows={2}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <span className="muted small">
          {toEmployee
            ? `${r.requester.name} sees this on the request in Ask HR, and gets an email and a Slack message saying HR commented (with a link, not the comment).`
            : `Only HR sees this.`}
        </span>
      </div>
      <div className="actions start">
        {r.status !== "in_progress" ? (
          <button type="button" disabled={busy} onClick={() => void update("in_progress")}>
            Mark in progress
          </button>
        ) : null}
        {r.status !== "resolved" ? (
          <button type="button" className="primary" disabled={busy} onClick={() => void update("resolved")}>
            Mark resolved
          </button>
        ) : (
          <button type="button" disabled={busy} onClick={() => void update("in_progress")}>
            Reopen
          </button>
        )}
        <button type="button" disabled={busy || !note.trim()} onClick={() => void update()}>
          {toEmployee ? "Add comment" : "Add note"}
        </button>
      </div>
    </div>
  );
}
